import { ref } from 'vue'
import { defineStore } from 'pinia'
import { findSupportedLocale } from '@architeia/utils'
import { KORA_WEB_LOCALES, type KoraWebLocale } from '@/i18n'
import { readLocaleHint, writeLocaleHint } from '@/i18n/localeHint'

interface AccountPreferenceSource {
  locale?: string | null
  timeZone?: string | null
}

/**
 * ADR-010 D3: the member's Kora account language and time zone. `locale` starts from the per-origin
 * hint and is replaced by Kora's value whenever the auth store loads the account or the member
 * saves new preferences (`apply`), which is what switches the UI live. Only `apply` writes the hint
 * and `clear` removes it (sign-out).
 */
export const useAccountPreferencesStore = defineStore('accountPreferences', () => {
  const locale = ref<KoraWebLocale | null>(readLocaleHint())
  const timeZone = ref<string | null>(null)

  function apply(user: AccountPreferenceSource): void {
    locale.value = findSupportedLocale(KORA_WEB_LOCALES, user.locale)
    timeZone.value = typeof user.timeZone === 'string' && user.timeZone ? user.timeZone : null
    writeLocaleHint(locale.value)
  }

  function clear(): void {
    locale.value = null
    timeZone.value = null
    writeLocaleHint(null)
  }

  return { locale, timeZone, apply, clear }
})
